/**
 * Warm-mic / instant-unmute policy (see
 * docs/superpowers/specs/2026-06-15-warm-mic-instant-unmute-design.md).
 *
 * "Warm" = the shared mic capture + denoiser graph stay alive while the user
 * is muted, and the publication is muted via `track.mute()` (Opus DTX, zero on
 * the wire). Unmute is then an instant `track.unmute()` instead of a cold
 * getUserMedia + RNNoise rebuild. The cost is that the OS mic stays captured
 * while muted (recording indicator lit, Windows comms-ducking active, invariant
 * #17) — accepted, bounded to live contexts + a 30 s solo-release + this flag.
 *
 * This module is intentionally LiveKit-free so the policy is unit-testable in
 * isolation; the stateful wiring lives in mediaContext.ts.
 */

export type RoomKind = 'channel' | 'dm'

export interface WarmEligibilityInput {
  kind: RoomKind
  /** Local participant's camera state. */
  cameraEnabled: boolean
  /** Total voice participants INCLUDING the local user (remote count + 1). */
  participantCount: number
}

/**
 * Should the mic stay WARM (vs COLD-released) while the user is muted? TRUE in a
 * live multi-party context where an unmute is plausibly imminent:
 *   - DM call: always (connect→disconnect; mute is rare there).
 *   - Channel: local camera ON, OR >=2 participants (me + >=1 other).
 */
export function computeWarmEligibility(input: WarmEligibilityInput): boolean {
  if (input.kind === 'dm') return true
  if (input.cameraEnabled) return true
  return input.participantCount >= 2
}

/**
 * Kill-switch for the whole warm-mic behaviour. Default ON; OFF only when
 * `localStorage 'voiceWarmMic' === 'false'`. When OFF, mute/unmute use the
 * legacy cold teardown path byte-for-byte.
 */
export function isWarmMicEnabled(): boolean {
  return typeof window === 'undefined' || localStorage.getItem('voiceWarmMic') !== 'false'
}

/**
 * Grace before a WARM→COLD teardown after the context stops being eligible
 * (covers a peer reconnect or a quick camera re-toggle). Applied uniformly to
 * every warm→cold transition. See spec §5.
 */
export const WARM_MIC_GRACE_MS = 30_000
